import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, CalendarIcon } from '@heroicons/react/20/solid';  

// Time Period Selector Component
export default function TimePeriodSelector({ selectedPeriod, onPeriodChange, fromDate, toDate, onFromDateChange, onToDateChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftFromDate, setDraftFromDate] = useState(fromDate || '');
  const [draftToDate, setDraftToDate] = useState(toDate || '');
  
  const periods = [
    'This Month',
    'Next Month',
    'Last Month',
    'This Quarter',
    'Last Quarter',
    'Year To Date',
    'Last Year',
    'Custom'
  ];

  const handlePeriodSelect = (period) => {
    onPeriodChange(period);
    setIsOpen(false);
    
    // Auto-calculate dates for non-custom periods
    if (period !== 'Custom') {
      const today = new Date();
      let from, to;
      
      switch (period) {
        case 'This Month':
          from = new Date(today.getFullYear(), today.getMonth(), 1);
          to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          break;
        case 'Next Month':
          from = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          to = new Date(today.getFullYear(), today.getMonth() + 2, 0);
          break;
        case 'Last Month':
          from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          to = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        case 'This Quarter':
          const quarterStart = Math.floor(today.getMonth() / 3) * 3;
          from = new Date(today.getFullYear(), quarterStart, 1);
          to = new Date(today.getFullYear(), quarterStart + 3, 0);
          break;
        case 'Last Quarter':
          const lastQuarterStart = Math.floor(today.getMonth() / 3) * 3 - 3;
          from = new Date(today.getFullYear(), lastQuarterStart, 1);
          to = new Date(today.getFullYear(), lastQuarterStart + 3, 0);
          break;
        case 'Year To Date':
          from = new Date(today.getFullYear(), 0, 1);
          to = today;
          break;
        case 'Last Year':
          from = new Date(today.getFullYear() - 1, 0, 1);
          to = new Date(today.getFullYear() - 1, 11, 31);
          break;
        default:
          return;
      }
      
      onFromDateChange(from.toISOString().split('T')[0]);
      onToDateChange(to.toISOString().split('T')[0]);
    }
  };

  useEffect(() => {
    setDraftFromDate(fromDate || '');
  }, [fromDate]);

  useEffect(() => {
    setDraftToDate(toDate || '');
  }, [toDate]);

  const handleApplyCustomDates = () => {
    onFromDateChange(draftFromDate);
    onToDateChange(draftToDate);
    handlePeriodSelect('Custom');
  };

  const isCustom = selectedPeriod === 'Custom';

  return (
    <div className="flex items-center space-x-4">
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          {selectedPeriod}
          <ChevronDownIcon className="ml-2 h-4 w-4" aria-hidden="true" />
        </button>
        
        {isOpen && (
          <div className="absolute left-0 z-10 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
            <div className="py-1">
              {periods.map((period) => (
                <button
                  key={period}
                  onClick={() => handlePeriodSelect(period)}
                  className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                    selectedPeriod === period
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-700'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Date inputs - always visible */}
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-2">
          <CalendarIcon className="h-4 w-4 text-gray-400" />
          <label className="text-sm font-medium text-gray-700">From:</label>
          <input
            type="date"
            value={draftFromDate}
            onChange={(e) => setDraftFromDate(e.target.value)}
            disabled={!isCustom}
            className={`rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
              isCustom 
                ? 'border-gray-300 bg-white text-gray-900' 
                : 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
            }`}
          />
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">To:</label>
          <input
            type="date"
            value={draftToDate}
            onChange={(e) => setDraftToDate(e.target.value)}
            disabled={!isCustom}
            className={`rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
              isCustom 
                ? 'border-gray-300 bg-white text-gray-900' 
                : 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
            }`}
          />
        </div>
        {isCustom && (
          <button
            type="button"
            onClick={handleApplyCustomDates}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            Apply
          </button>
        )}
      </div>
    </div>
  );
}